/* eslint-disable no-unused-vars */
import React, {
  FC,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useState
} from 'react';
import { Box, Button, Card, Grid, TextField } from '@material-ui/core';

import {
  SearchKeyword,
  SearchKeywordFilterGroup
} from 'src/components/filters';
import { DatePickerRangeComponent } from 'src/components';
import { MaterialUiPickersDate } from '@material-ui/pickers/typings/date';
import { dateToday, transformerDateField } from 'src/utils';
import { ChequeListPayload } from 'src/redux/slices/cheque-management';
import { useCheque } from 'src/hooks/cheque-management/use-cheque';
import { LiabilityFilters } from './LiabilityFilters';
import { Autocomplete } from '@material-ui/lab';
import { Supplier } from 'src/types/supplier';
import { unwrapResult } from '@reduxjs/toolkit';
import { slices, useAppDispatch } from 'src/redux';
import { isEmpty } from 'lodash';
import { CustomKeyboardEvent } from 'src/types';
import { useSearchParams } from 'react-router-dom';

const searchFilter = ['Cheque No', 'DR No', 'P.O. No'];

const { actions: supplierActions } = slices.supplier;

interface Props {
  onHandleAppliedFilter: () => void;
}

const Component: FC<Props> = ({ onHandleAppliedFilter }) => {
  const dispatch = useAppDispatch();
  const [searchParams] = useSearchParams();
  const {
    chequeListParams,
    fetchChequeList,
    setChequeListParams
  } = useCheque();

  const { cheque_no, dr_no, po_no } = useMemo(() => {
    return Object.fromEntries(searchParams.entries());
  }, [searchParams]);

  const initialFilter = po_no
    ? 'P.O. No'
    : dr_no
    ? 'DR No'
    : cheque_no
    ? 'Cheque No'
    : searchFilter[0];

  const [searchLabel, setSearchLabel] = useState<string>(initialFilter);
  const [fromDate, setFromDate] = useState<string | undefined>(
    chequeListParams?.pdc_date_from
  );
  const [toDate, setToDate] = useState<string | undefined>(
    chequeListParams?.pdc_date_to
  );
  const [toEncodedDate, setToEncodedDate] = useState<string | undefined>(
    chequeListParams?.created_at_to
  );
  const [fromEncodedDate, setFromEncodedDate] = useState<string | undefined>(
    chequeListParams?.created_at_from
  );
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);

  const [chequeFilterParams, setChequeFilterParams] = useState<
    ChequeListPayload
  >(chequeListParams);

  const onChangeFilterAction = useCallback((filter?: string) => {
    setSearchLabel(filter || searchFilter[0]);
  }, []);

  const onChangeKeyword = useCallback(
    (value: string) => {
      setChequeFilterParams((prev) => ({
        ...prev,
        cheque_no: searchLabel === 'Cheque No' ? value : undefined,
        dr_no: searchLabel === 'DR No' ? value : undefined,
        po_no: searchLabel === 'P.O. No' ? value : undefined
      }));
    },
    [searchLabel]
  );

  const onChangedDate = useCallback(
    (date: MaterialUiPickersDate | null, field: 'from' | 'to') => {
      const transformedDate = date ? transformerDateField(date) : undefined;
      if (field === 'to') {
        setToDate(transformedDate);
        setChequeFilterParams((prev) => ({
          ...prev,
          pdc_date_to: transformedDate
        }));
      } else if (field === 'from') {
        setFromDate(transformedDate);
        setChequeFilterParams((prev) => ({
          ...prev,
          pdc_date_from: transformedDate
        }));
      }
    },
    []
  );

  const onChangedEncodedDate = useCallback(
    (date: MaterialUiPickersDate | null, field: 'from' | 'to') => {
      const transformedDate = date ? transformerDateField(date) : undefined;
      if (field === 'to') {
        setToEncodedDate(transformedDate);
        setChequeFilterParams((prev) => ({
          ...prev,
          created_at_to: transformedDate
        }));
      } else if (field === 'from') {
        setFromEncodedDate(transformedDate);
        setChequeFilterParams((prev) => ({
          ...prev,
          created_at_from: transformedDate
        }));
      }
    },
    []
  );

  const onApplyFilter = (params?: ChequeListPayload) => {
    // remove undefined values
    const cleanedParams = Object.fromEntries(
      Object.entries(params || {}).filter(
        ([, value]) => value !== undefined && value !== null && value !== ''
      )
    );
    onHandleAppliedFilter();
    setChequeListParams({ ...cleanedParams, page: 1 });
    fetchChequeList({
      ...cleanedParams,
      page: 1
    });
  };

  const getSuppliers = useCallback(async () => {
    const getSuppliersResponse = unwrapResult(
      await dispatch(
        supplierActions.getSuppliersThunk({ keyword: '', limit: 999999 })
      )
    );

    if (getSuppliersResponse?.success) {
      setSuppliers(getSuppliersResponse?.originalData.data || []);
    }
  }, [dispatch]);

  const onKeyPress = (e: CustomKeyboardEvent) => {
    if (e.key === 'Enter') {
      onApplyFilter(chequeFilterParams);
    }
  };

  useEffect(() => {
    // initializer params
    if (chequeListParams && Object.keys(chequeListParams).length > 0) {
      setChequeFilterParams(chequeListParams);
      setFromDate(chequeListParams?.pdc_date_from);
      setToDate(chequeListParams?.pdc_date_to);
      setFromEncodedDate(chequeListParams?.created_at_from);
      setToEncodedDate(chequeListParams?.created_at_to);
    }
  }, [chequeListParams]);

  useEffect(() => {
    if (isEmpty(suppliers)) {
      getSuppliers();
    }
  }, [getSuppliers, suppliers]);

  return (
    <Card style={{ marginTop: 10, padding: 15 }}>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <SearchKeywordFilterGroup
            initialFilter={searchLabel}
            filterOptions={searchFilter}
            onHandleFilterSearhBy={onChangeFilterAction}
          />
        </Grid>

        <Grid item xs={12} md={4}>
          <Box display="flex" alignItems="center" justifyContent="center">
            <SearchKeyword
              value={
                chequeFilterParams?.cheque_no ||
                chequeFilterParams?.dr_no ||
                chequeFilterParams?.po_no ||
                ''
              }
              searchLabel={`Search By ${searchLabel}`}
              onHandleSearchKeyword={onChangeKeyword}
              onEnterPress={onKeyPress}
            />
          </Box>
        </Grid>
        <Grid item xs={12} md={4}>
          <LiabilityFilters
            value={chequeListParams?.liability ?? 2}
            onChange={(liability: number | undefined) =>
              setChequeListParams({ ...chequeListParams, liability })
            }
          />
        </Grid>

        <Grid item xs={12} md={4}>
          <Autocomplete
            id="supplier"
            options={suppliers || []}
            getOptionLabel={(option) => option?.name || ''}
            value={
              suppliers.find(
                (supplier) => supplier.id === chequeListParams?.supplier_id
              ) || null
            }
            onChange={(e: any, val) =>
              setChequeListParams({
                ...chequeFilterParams,
                supplier_id: val?.id
              })
            }
            renderInput={(params) => (
              <TextField
                {...params}
                name="supplier"
                label="Supplier"
                variant="outlined"
              />
            )}
          />
        </Grid>

        <Grid item xs={12} md={4}>
          <Box display="flex" alignItems="center" justifyContent="center">
            <DatePickerRangeComponent
              fullwidth={true}
              hasNoDateToday={true}
              fromDateValue={fromDate || null}
              toDateValue={toDate || null}
              dateToLabel="PDC Date To"
              dateFromLabel="PDC Date From"
              onChangeToDate={(date) => onChangedDate(date, 'to')}
              onChangeFromDate={(date) => onChangedDate(date, 'from')}
            />
          </Box>
        </Grid>

        <Grid item xs={12} md={4}>
          <Box display="flex" alignItems="center" justifyContent="center">
            <DatePickerRangeComponent
              fullwidth={true}
              fromDateValue={fromEncodedDate || null}
              toDateValue={toEncodedDate || null}
              toDateMin={fromEncodedDate ?? undefined}
              fromDateMax={dateToday()}
              dateToLabel="Date Encoded To"
              dateFromLabel="Date Encoded From"
              onChangeToDate={(date) => onChangedEncodedDate(date, 'to')}
              onChangeFromDate={(date) => onChangedEncodedDate(date, 'from')}
            />
          </Box>
        </Grid>

        <Grid item xs={12} md={2} style={{ display: 'flex' }}>
          <Button
            variant="contained"
            color="primary"
            fullWidth
            onClick={() => {
              onApplyFilter(chequeFilterParams);
            }}
          >
            Apply Filter
          </Button>
        </Grid>
      </Grid>
    </Card>
  );
};

export const Toolbar = memo(Component);
